// Quality & Operations Portal — data layer.
//
// Read/write helpers for the DD Portal tables (test_cases, test_runs,
// test_results, defects, dd_artifacts, build_releases). Every query
// goes through the standard Supabase client; RLS gates access to
// corporate_admin operators.

import { supabase } from './supabase';
import { isCell } from './cell/config';
import { cellPost } from './cell/api';

// CELL (M28): the QA portal's test loop on AWS (Cora 2026-10-29: test loop only, ONE route). Every test-loop call below is
// POST /v1/pro-hub/qa/<op> (tenean-infra api/src/routes/pro-hub/qa.ts), one definer door of cell migration 0212 each, gated
// by production's own app_is_corporate_admin(). Defects, build releases and DD artifacts are NOT on AWS: their calls refuse
// with QA_NOT_ON_AWS and the page shows that line instead of the panel.
export const QA_NOT_ON_AWS = 'Not on AWS: defects, build releases and DD artifacts stayed with the old platform.';
async function qaOp<T>(op: string, body: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await cellPost<{ data: T }>(`/v1/pro-hub/qa/${op}`, body);
  if (error) throw new Error(error.message);
  return (data?.data ?? null) as T;
}
/**
 * CELL (M28, Cora 2026-10-30 ruling (i)): the QA portal's DISPLAY gate on the cell. useOperator is null on the cell (the
 * other /admin/* operator tools stay off the AWS Pro Hub), so the portal asks the cell instead: ONE read-only op,
 * dashboard_summary (qa_dashboard_summary, STABLE, migration 0212, whose first statement is qa_gate() over production's
 * app_is_corporate_admin()). Answered = admitted. ANY failure (42501, a network error, a 5xx, no answer in time) = refused:
 * fail closed. The door itself stays the real guard; this only decides what the page shows.
 */
export async function cellQaAdmitted(timeoutMs = 10_000): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      qaOp<unknown>('dashboard_summary'),
      new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('timeout')), timeoutMs); }),
    ]);
    return true;
  } catch {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
function notOnAws(): never {
  throw new Error(QA_NOT_ON_AWS);
}

// ─── Types ─────────────────────────────────────────────────────────────

export type TestType = 'M' | 'A' | 'H' | 'U';
export type Priority = 'P0' | 'P1' | 'P2' | 'DEV';
export type RunEnvironment =
  | 'DEV-Sim' | 'DEV-Device' | 'TestFlight' | 'Production' | 'Staging'
  | 'GitHub Actions' | 'CI';
export type ResultStatus = 'pass' | 'fail' | 'blocked' | 'skip';
export type DefectSeverity = 'critical' | 'high' | 'medium' | 'low';
export type DefectStatus =
  | 'open' | 'investigating' | 'fixed' | 'verified' | 'closed' | 'wontfix';

export type TestCase = {
  id: string;
  section: string;
  subsection: string | null;
  title: string;
  preconditions: string | null;
  steps: string;
  expected: string;
  test_type: TestType;
  priority: Priority;
  notes: string | null;
  validates_commit: string | null;
  harness_action: Record<string, unknown> | null;
  /** Where the write lands for verification (Supabase table, Storage
   *  bucket, external service). NULL for read-only / nav tests. */
  persistence_target: string | null;
  display_order: number | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
};

export type TestRun = {
  id: string;
  run_id: string;
  build_version: string | null;
  build_commit: string | null;
  environment: RunEnvironment | null;
  tester_email: string | null;
  is_acceptance_run: boolean;
  started_at: string;
  completed_at: string | null;
  notes: string | null;
};

export type TestResult = {
  id: string;
  run_id: string;
  test_case_id: string;
  status: ResultStatus;
  observed: string | null;
  duration_seconds: number | null;
  recorded_at: string;
  recorded_by: string | null;
};

export type Defect = {
  id: string;
  defect_number: string;
  title: string;
  description: string | null;
  severity: DefectSeverity;
  status: DefectStatus;
  test_case_id: string | null;
  test_run_id: string | null;
  test_result_id: string | null;
  reported_by: string | null;
  reported_at: string;
  fix_commit_sha: string | null;
  fixed_in_build: string | null;
  fixed_at: string | null;
  verified_in_run_id: string | null;
  verified_at: string | null;
  fix_notes: string | null;
  resolution_notes: string | null;
  created_at: string;
  updated_at: string;
};

export type DDArtifact = {
  id: string;
  category: 'security' | 'architecture' | 'operations' | 'code'
          | 'compliance' | 'test' | 'incidents' | 'ip' | 'people';
  title: string;
  description: string | null;
  url: string | null;
  source_type: string | null;
  badge: string | null;
  display_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
};

export type BuildRelease = {
  id: string;
  build_number: string;
  commit_sha: string | null;
  channel: 'TestFlight' | 'AppStore' | 'Internal' | 'Production';
  released_at: string;
  notes: string | null;
  acceptance_run_id: string | null;
  is_current: boolean;
};

// WorkflowHealth + listWorkflowHealth removed 2026-07-11 (N8N-DECOMM).

// ─── Test cases ───────────────────────────────────────────────────────

export async function listTestCases(): Promise<TestCase[]> {
  // CELL (M28): cases_list
  if (isCell()) return qaOp<TestCase[]>('cases_list');

  const { data, error } = await supabase
    .from('test_cases')
    .select('*')
    .eq('is_archived', false)
    .order('display_order', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// ─── Test runs ────────────────────────────────────────────────────────

export async function listTestRuns(limit = 50): Promise<TestRun[]> {
  // CELL (M28): runs_list
  if (isCell()) return qaOp<TestRun[]>('runs_list', { limit });

  const { data, error } = await supabase
    .from('test_runs')
    .select('*')
    .order('started_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

export async function getTestRun(id: string): Promise<TestRun | null> {
  // CELL (M28): run_get
  if (isCell()) return qaOp<TestRun | null>('run_get', { id });

  const { data, error } = await supabase
    .from('test_runs')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/**
 * Create a new test run. run_id auto-generated as
 * RUN-YYYYMMDD-<6char-base36> if not supplied.
 */
export async function createTestRun(input: {
  build_version?: string | null;
  build_commit?: string | null;
  environment?: RunEnvironment | null;
  tester_email?: string | null;
  is_acceptance_run?: boolean;
  notes?: string | null;
}): Promise<TestRun> {
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const suffix = Math.random().toString(36).slice(2, 8);
  const run_id = `RUN-${today}-${suffix}`;
  // CELL (M28): run_create — the same run_id, made here as production does
  if (isCell()) return qaOp<TestRun>('run_create', { run_id, ...input });
  const { data, error } = await supabase
    .from('test_runs')
    .insert({ run_id, ...input })
    .select('*')
    .single();
  if (error) throw error;
  return data!;
}

export async function completeTestRun(id: string, notes?: string) {
  // CELL (M28): run_complete
  if (isCell()) { await qaOp('run_complete', { id, notes: notes || null }); return; }

  const { error } = await supabase
    .from('test_runs')
    .update({
      completed_at: new Date().toISOString(),
      ...(notes ? { notes } : {}),
    })
    .eq('id', id);
  if (error) throw error;
}

// ─── Test results ─────────────────────────────────────────────────────

export async function listResultsForRun(runId: string): Promise<TestResult[]> {
  // CELL (M28): results_list
  if (isCell()) return qaOp<TestResult[]>('results_list', { run_id: runId });

  const { data, error } = await supabase
    .from('test_results')
    .select('*')
    .eq('run_id', runId)
    .order('recorded_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function recordResult(input: {
  run_id: string;
  test_case_id: string;
  status: ResultStatus;
  observed?: string | null;
  duration_seconds?: number | null;
  recorded_by?: string | null;
}): Promise<TestResult> {
  // CELL (M28): result_record (upsert on run_id, test_case_id)
  if (isCell()) return qaOp<TestResult>('result_record', input);

  // Upsert on (run_id, test_case_id) — re-running a case in the same run
  // overwrites the prior outcome.
  const { data, error } = await supabase
    .from('test_results')
    .upsert(input, { onConflict: 'run_id,test_case_id' })
    .select('*')
    .single();
  if (error) throw error;
  return data!;
}

// ─── Defects ──────────────────────────────────────────────────────────

export async function listDefects(): Promise<Defect[]> {
  // CELL (M28): not on AWS (Cora 2026-10-29)
  if (isCell()) notOnAws();

  const { data, error } = await supabase
    .from('defects')
    .select('*')
    .order('reported_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function fileDefect(input: {
  title: string;
  description?: string | null;
  severity: DefectSeverity;
  test_case_id?: string | null;
  test_run_id?: string | null;
  test_result_id?: string | null;
  reported_by?: string | null;
}): Promise<Defect> {
  // CELL (M28): not on AWS (Cora 2026-10-29)
  if (isCell()) notOnAws();

  const { data, error } = await supabase
    .from('defects')
    .insert({ ...input, status: 'open' })
    .select('*')
    .single();
  if (error) throw error;
  return data!;
}

export async function updateDefect(id: string, patch: Partial<Defect>) {
  // CELL (M28): not on AWS (Cora 2026-10-29)
  if (isCell()) notOnAws();

  const { error } = await supabase
    .from('defects')
    .update(patch)
    .eq('id', id);
  if (error) throw error;
}

// ─── DD artifacts ─────────────────────────────────────────────────────

export async function listDDArtifacts(): Promise<DDArtifact[]> {
  // CELL (M28): not on AWS (Cora 2026-10-29)
  if (isCell()) notOnAws();

  const { data, error } = await supabase
    .from('dd_artifacts')
    .select('*')
    .eq('is_published', true)
    .order('category', { ascending: true })
    .order('display_order', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// ─── Build releases ───────────────────────────────────────────────────

export async function listBuildReleases(): Promise<BuildRelease[]> {
  // CELL (M28): not on AWS (Cora 2026-10-29)
  if (isCell()) notOnAws();

  const { data, error } = await supabase
    .from('build_releases')
    .select('*')
    .order('released_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

// ─── Workflow health (n8n + Edge Functions) ───────────────────────────


// ─── Aggregates / KPIs ────────────────────────────────────────────────

export type RunPassRate = {
  run_id: string;
  started_at: string;
  is_acceptance: boolean;
  pass: number;
  fail: number;
  blocked: number;
  skip: number;
  total: number;
  pct: number;       // 0..1
};

/**
 * Last N test runs with aggregated pass-rate. Drives the dashboard
 * sparkline + the per-run mini-stats.
 */
export async function getRecentRunsPassRates(limit = 20): Promise<RunPassRate[]> {
  type RunRow = { id: string; run_id: string; started_at: string; is_acceptance_run: boolean };
  let runs: RunRow[] | null;
  let results: Array<{ run_id: string; status: string }> | null;
  if (isCell()) {
    // CELL (M28): runs_pass_rates — the same two reads in one door; the counting below is unchanged
    const r = await qaOp<{ runs: RunRow[] | null; results: Array<{ run_id: string; status: string }> | null }>('runs_pass_rates', { limit });
    runs = r?.runs ?? [];
    if (!runs.length) return [];
    results = r?.results ?? [];
  } else {
    const { data: runRows, error: rErr } = await supabase
      .from('test_runs')
      .select('id, run_id, started_at, is_acceptance_run')
      .order('started_at', { ascending: false })
      .limit(limit);
    if (rErr) throw rErr;
    runs = runRows;
    if (!runs?.length) return [];

    const ids = runs.map(r => r.id);
    // CELL (M28): production path only (the cell build answered above)
    const { data: resultRows, error: resErr } = await supabase
      .from('test_results')
      .select('run_id, status')
      .in('run_id', ids);
    if (resErr) throw resErr;
    results = resultRows;
  }

  const counts: Record<string, { pass: number; fail: number; blocked: number; skip: number }> = {};
  for (const r of results ?? []) {
    const c = counts[r.run_id] ||= { pass: 0, fail: 0, blocked: 0, skip: 0 };
    (c as any)[r.status] = ((c as any)[r.status] ?? 0) + 1;
  }

  return runs!.map(r => {
    const c = counts[r.id] ?? { pass: 0, fail: 0, blocked: 0, skip: 0 };
    const total = c.pass + c.fail + c.blocked + c.skip;
    return {
      run_id: r.run_id,
      started_at: r.started_at,
      is_acceptance: r.is_acceptance_run,
      ...c,
      total,
      pct: total > 0 ? c.pass / total : 0,
    };
  });
}

export type SectionStats = {
  section: string;
  total: number;
  p0: number;
  p1: number;
  p2: number;
  dev: number;
  // latest-run roll-ups, populated when results exist
  passed: number;
  failed: number;
  blocked: number;
  untested: number;
};

/**
 * Per-section roll-up: case counts by priority, plus latest-run
 * pass/fail/blocked split. Drives the Cases-tab section heatmap.
 */
export async function getSectionStats(): Promise<SectionStats[]> {
  type CaseRow = { id: string; section: string; priority: Priority };
  let cases: CaseRow[] | null;
  let latest: Array<{ test_case_id: string; latest_status: string | null }> | null;
  if (isCell()) {
    // CELL (M28): section_stats — the cases and the latest-result view in one door; the roll-up below is unchanged
    const r = await qaOp<{ cases: CaseRow[] | null; latest: Array<{ test_case_id: string; latest_status: string | null }> | null }>('section_stats');
    cases = r?.cases ?? [];
    if (!cases.length) return [];
    latest = r?.latest ?? [];
  } else {
    const { data: caseRows, error } = await supabase
      .from('test_cases')
      .select('id, section, priority')
      .eq('is_archived', false);
    if (error) throw error;
    cases = caseRows;
    if (!cases?.length) return [];

    // Latest result per case (use the convenience view)
    // CELL (M28): production path only (the cell build answered above)
    const { data: latestRows } = await supabase
      .from('v_test_case_latest_result')
      .select('test_case_id, latest_status');
    latest = latestRows;
  }
  const latestByCase: Record<string, string | null> = {};
  for (const r of latest ?? []) {
    latestByCase[r.test_case_id] = r.latest_status;
  }

  const map: Record<string, SectionStats> = {};
  for (const c of cases!) {
    const m = map[c.section] ||= {
      section: c.section, total: 0, p0: 0, p1: 0, p2: 0, dev: 0,
      passed: 0, failed: 0, blocked: 0, untested: 0,
    };
    m.total++;
    if (c.priority === 'P0')  m.p0++;
    else if (c.priority === 'P1')  m.p1++;
    else if (c.priority === 'P2')  m.p2++;
    else if (c.priority === 'DEV') m.dev++;
    const s = latestByCase[c.id];
    if (s === 'pass')           m.passed++;
    else if (s === 'fail')      m.failed++;
    else if (s === 'blocked')   m.blocked++;
    else                        m.untested++;
  }

  return Object.values(map).sort((a, b) => a.section.localeCompare(b.section));
}

export type DefectStats = {
  byStatus: Record<DefectStatus, number>;
  bySeverity: Record<DefectSeverity, number>;
  ageBuckets: { label: string; count: number }[];   // 0-1d, 1-7d, 7-30d, 30d+
  totalOpen: number;
};

/**
 * Defect summary: status histogram + severity histogram + open-defect
 * age buckets. Drives the Defects-tab visualizations.
 */
export async function getDefectStats(): Promise<DefectStats> {
  // CELL (M28): not on AWS (Cora 2026-10-29)
  if (isCell()) notOnAws();

  const { data, error } = await supabase
    .from('v_defect_aging')
    .select('status, severity, age_days_open');
  if (error) throw error;

  const empty = (): Record<string, number> => ({});
  const byStatus = empty() as Record<DefectStatus, number>;
  const bySeverity = empty() as Record<DefectSeverity, number>;
  for (const s of ['open','investigating','fixed','verified','closed','wontfix'] as const)  byStatus[s] = 0;
  for (const s of ['critical','high','medium','low'] as const) bySeverity[s] = 0;

  const buckets = { '0-1d': 0, '1-7d': 0, '7-30d': 0, '30d+': 0 };
  let totalOpen = 0;

  for (const d of data ?? []) {
    byStatus[d.status as DefectStatus] = (byStatus[d.status as DefectStatus] ?? 0) + 1;
    bySeverity[d.severity as DefectSeverity] = (bySeverity[d.severity as DefectSeverity] ?? 0) + 1;
    if (d.age_days_open != null) {
      totalOpen++;
      const a = d.age_days_open as number;
      if (a < 1)        buckets['0-1d']++;
      else if (a < 7)   buckets['1-7d']++;
      else if (a < 30)  buckets['7-30d']++;
      else              buckets['30d+']++;
    }
  }

  return {
    byStatus,
    bySeverity,
    ageBuckets: Object.entries(buckets).map(([label, count]) => ({ label, count })),
    totalOpen,
  };
}

export type DashboardSummary = {
  totalCases: number;
  p0Cases: number;
  p1Cases: number;
  automatableCases: number;
  totalRuns: number;
  latestRunPassRate: number | null;
  latestRunAt: string | null;
  openDefects: number;
  criticalOpenDefects: number;
  defectsFixedAllTime: number;
  averageTimeToFixDays: number | null;
  artifactsCount: number;
  /** The cell build: defects and DD artifacts are not on AWS, so their four numbers are not real there (Cora 2026-10-29). */
  defectsNotOnAws?: boolean;
};

export async function getDashboardSummary(): Promise<DashboardSummary> {
  if (isCell()) {
    // CELL (M28): dashboard_summary — the test loop's half (cases, runs, the latest run's results); the same arithmetic as
    // below. Defects and DD artifacts are not on AWS: zero, with defectsNotOnAws so the page says so instead of a number.
    const r = await qaOp<{
      cases: Array<{ id: string; priority: Priority; test_type: TestType }> | null; runs_count: number | null;
      latest_run: { id: string; run_id: string; started_at: string } | null; latest_results: Array<{ status: string }> | null;
    }>('dashboard_summary');
    const cases = r?.cases ?? [];
    const results = r?.latest_results ?? [];
    return {
      totalCases: cases.length,
      p0Cases: cases.filter(c => c.priority === 'P0').length,
      p1Cases: cases.filter(c => c.priority === 'P1').length,
      automatableCases: cases.filter(c => c.test_type === 'A' || c.test_type === 'U').length,
      totalRuns: r?.runs_count ?? 0,
      latestRunPassRate: r?.latest_run && results.length > 0 ? results.filter(x => x.status === 'pass').length / results.length : null,
      latestRunAt: r?.latest_run?.started_at ?? null,
      openDefects: 0, criticalOpenDefects: 0, defectsFixedAllTime: 0, averageTimeToFixDays: null, artifactsCount: 0,
      defectsNotOnAws: true,
    };
  }
  const [
    casesRes,
    runsRes,
    defectsRes,
    artifactsRes,
    latestRunRes,
  ] = await Promise.all([
    // CELL (M28): production path only (the cell build answered above)
    supabase
      .from('test_cases')
      .select('id, priority, test_type', { count: 'exact', head: false })
      .eq('is_archived', false),
    // CELL (M28): production path only (the cell build answered above)
    supabase
      .from('test_runs')
      .select('id', { count: 'exact', head: true }),
    // CELL (M28): production path only (the cell build answered above)
    supabase
      .from('v_defect_aging')
      .select('status, severity, time_to_fix_days'),
    // CELL (M28): production path only (the cell build answered above)
    supabase
      .from('dd_artifacts')
      .select('id', { count: 'exact', head: true })
      .eq('is_published', true),
    // CELL (M28): production path only (the cell build answered above)
    supabase
      .from('test_runs')
      .select('id, run_id, started_at')
      .order('started_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);

  if (casesRes.error)     throw casesRes.error;
  if (runsRes.error)      throw runsRes.error;
  if (defectsRes.error)   throw defectsRes.error;
  if (artifactsRes.error) throw artifactsRes.error;

  const cases = casesRes.data ?? [];
  const totalCases = cases.length;
  const p0Cases = cases.filter(c => c.priority === 'P0').length;
  const p1Cases = cases.filter(c => c.priority === 'P1').length;
  const automatableCases = cases.filter(c => c.test_type === 'A' || c.test_type === 'U').length;

  const defects = (defectsRes.data ?? []) as Array<{
    status: string; severity: string; time_to_fix_days: number | null;
  }>;
  const openDefects = defects.filter(d =>
    !['closed', 'verified', 'wontfix'].includes(d.status)
  ).length;
  const criticalOpenDefects = defects.filter(d =>
    !['closed', 'verified', 'wontfix'].includes(d.status) && d.severity === 'critical'
  ).length;
  const fixed = defects.filter(d => ['fixed', 'verified', 'closed'].includes(d.status));
  const fixedWithTime = fixed.filter(d => d.time_to_fix_days != null);
  const averageTimeToFixDays = fixedWithTime.length
    ? fixedWithTime.reduce((s, d) => s + (d.time_to_fix_days ?? 0), 0) / fixedWithTime.length
    : null;

  let latestRunPassRate: number | null = null;
  let latestRunAt: string | null = latestRunRes.data?.started_at ?? null;
  if (latestRunRes.data) {
    // CELL (M28): production path only (the cell build answered above)
    const { data: results } = await supabase
      .from('test_results')
      .select('status')
      .eq('run_id', latestRunRes.data.id);
    if (results && results.length > 0) {
      const passed = results.filter(r => r.status === 'pass').length;
      latestRunPassRate = passed / results.length;
    }
  }

  return {
    totalCases,
    p0Cases,
    p1Cases,
    automatableCases,
    totalRuns: runsRes.count ?? 0,
    latestRunPassRate,
    latestRunAt,
    openDefects,
    criticalOpenDefects,
    defectsFixedAllTime: fixed.length,
    averageTimeToFixDays,
    artifactsCount: artifactsRes.count ?? 0,
  };
}
